/* eslint-disable no-plusplus */
/* eslint-disable no-restricted-syntax */

import { store } from 'redux/store';

import operationTypes from 'constants/operationTypes';

import { replaceAccentedVowels } from 'utils/replaceAccentedVowels';
import { getOperationDataForSEPA } from '../api/getOperationDataForSEPA';

const ROW_LENGTH = 600;

const parseSociety = (society) => ({
  name: replaceAccentedVowels(society?.name || ''),
  address: {
    street: society?.legalAddress?.line1 || '',
    postalCode: society?.legalAddress?.zip || '',
    city: society?.legalAddress?.city || '',
    province: society?.legalAddress?.province || '',
  },
  country: society?.legalAddress?.country || '',
  accountNumber: society?.additional?.bankAccountNumber || '',
  identifier: society?.additional?.bankIdentifier || '',
  cif: society?.cif || '',
});

const paymentConceptsByType = {
  [operationTypes.DIVIDENDS_DISTRIBUTION]: 'PAGO DE DIVIDENDOS',
  [operationTypes.CAPITAL_INCREASE]: 'AUMENTO DE CAPITAL',
  [operationTypes.CAPITAL_DECREASE]: 'REDUCCIÓN DE CAPITAL',
  [operationTypes.OTHER]: 'OTROS',
};

const LATIN1_REPLACEMENTS = {
  '\u2018': "'",
  '\u2019': "'",
  '\u201A': ',',
  '\u201B': "'", // ‘ ’ ‚ ‛
  '\u201C': '"',
  '\u201D': '"',
  '\u201E': '"',
  '\u201F': '"', // “ ” „ ‟
  '\u2010': '-',
  '\u2011': '-',
  '\u2012': '-', // ‐ ‑ ‒
  '\u2013': '-',
  '\u2014': '-',
  '\u2015': '-', // – — ―
  '\u2032': "'",
  '\u2033': '"', // ′ ″
  '\u00A0': ' ',
  '\u2007': ' ',
  '\u2009': ' ',
  '\u202F': ' ', // nbsp & friends
  '\u2022': '.', // •
};

const sanitizeLatin1Inline = (content) =>
  [...content].map((ch) => LATIN1_REPLACEMENTS[ch] ?? ch).join('');

const padNumber = (num, length) => num.toString().padStart(length, '0');

const formatAmount = (amount, padLenth = 11) =>
  `${padNumber(Math.round(parseFloat(amount) * 100), padLenth)}3`;

const formatTotalAmount = (amount, padLenth = 12) =>
  padNumber(Math.round(parseFloat(amount) * 100), padLenth);

const formatDate = (date = new Date()) =>
  date.toISOString().slice(0, 10).replace(/-/g, '');

const padString = (str, length, char = ' ') =>
  (str || '').toString().padEnd(length, char);

const cleanAccountNumber = (account) =>
  (account || '').replace(/[^A-Z0-9]/g, '');

const generateRecord01 = (companyData, currentDate) => {
  const {
    name,
    address: { street, postalCode, city, province },
    country,
    accountNumber,
    identifier,
    cif,
  } = companyData;

  const record = [
    '01',
    'ORD',
    identifier,
    '001',
    cif,
    'DIV',
    currentDate,
    currentDate,
    'A',
    cleanAccountNumber(accountNumber),
    padString('', 10), // Empty space
    '0',
    padString(name, 50),
    padString('', 20), // Empty space
    padString(street, 40),
    padString('', 10),
    padString(postalCode, 5),
    padString('', 1),
    padString(city, 30),
    padString('', 14),
    padString(province, 30),
    padString('', 10),
    padString(country || 'ES', 2),
  ].join('');

  const currentLength = record.length;
  const remainingSpaces = ROW_LENGTH - currentLength;

  return remainingSpaces > 0
    ? `${record}${padString('', remainingSpaces)}`
    : record;
};

const generateRecord02 = (companyData) => {
  const { identifier, cif } = companyData;

  return [
    '02',
    'SCT',
    identifier,
    cif,
    'DIV',
    padString('', 578), // Remaining spaces
  ].join('');
};

const generateRecord = ({
  prefix,
  type,
  totalAmount,
  recordCount,
  additionalPadding,
}) => {
  const record = [
    prefix,
    type,
    formatTotalAmount(totalAmount, 17),
    padNumber(recordCount, 8),
    padNumber(recordCount + additionalPadding, 10),
  ].join('');

  const currentLength = record.length;
  const remainingSpaces = ROW_LENGTH - currentLength;

  return remainingSpaces > 0
    ? `${record}${padString('', remainingSpaces)}`
    : record;
};

const generateResumeRecord0 = (totalAmount, recordCount) =>
  generateRecord({
    prefix: '04',
    type: 'SCT',
    totalAmount,
    recordCount,
    additionalPadding: 2,
  });

const generateResumeRecord1 = (totalAmount, recordCount) =>
  generateRecord({
    prefix: '99',
    type: 'ORD',
    totalAmount,
    recordCount,
    additionalPadding: 4,
  });

const encodeLatin1 = (content) => {
  const bytes = new Uint8Array(content.length);
  for (let i = 0; i < content.length; i++) {
    const code = content.charCodeAt(i);
    if (code > 0xff) {
      const hex = code.toString(16).toUpperCase().padStart(4, '0');
      throw new Error(
        `Carácter no representable en ISO-8859-1 en la posición ${i}: "${content[i]}" (U+${hex})`
      );
    }
    bytes[i] = code;
  }
  return bytes;
};

// Main SEPA file generator function
const generateSEPAFile = async ({
  operationId,
  society,
  operationType = operationTypes.OTHER,
}) => {
  try {
    let content = '';
    const currentDate = formatDate();
    const companyData = parseSociety(society);
    const paymentConcept = paymentConceptsByType[operationType] || 'OTROS';

    const headerRecord = generateRecord01(companyData, currentDate);
    content += `${headerRecord}\n`;

    const controlRecord = generateRecord02(companyData);
    content += `${controlRecord}\n`;

    const { amountContent, totalAmount, recordCount } = await store.dispatch(
      getOperationDataForSEPA({
        operationId,
        paymentConcept,
      })
    );

    content += amountContent;

    const resumeRecord0 = generateResumeRecord0(totalAmount, recordCount);
    const resumeRecord1 = generateResumeRecord1(totalAmount, recordCount);
    content += `${resumeRecord0}\n`;
    content += `${resumeRecord1}\n`;

    const sanitized = sanitizeLatin1Inline(content);
    const bytes = encodeLatin1(sanitized);
    const blob = new Blob([bytes], { type: 'text/plain;charset=ISO-8859-1' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sepa_transfer_${currentDate}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return {
      success: true,
      recordCount,
      totalAmount,
      filename: `sepa_transfer_${currentDate}.txt`,
    };
  } catch (error) {
    throw error instanceof Error ? error : new Error(String(error));
  }
};

export {
  generateSEPAFile,
  formatAmount,
  formatDate,
  padString,
  cleanAccountNumber,
};
